import React, { useEffect, useState } from "react";
import Text from "@/components/ui/text";
import Modal from "@/components/controls/Modal";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import TextareaAutosize from "react-textarea-autosize";
import { useUpdatePeopleStatusMutation } from "@/entities/people/hooks";
import { PersonData, PersonStatus } from "@/entities/people/types";
import ClickableTooltip from "@/components/controls/ClickableTooltip";
import { isEmpty } from "lodash";

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  mode: "disqualify" | "requalify" | "edit";
  initialReason: string;
  onUpdatePerson: (newPersonData: PersonData) => void;
  person?: PersonData;
}

const PersonQualificationModal: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  mode,
  initialReason,
  onUpdatePerson,
  person
}) => {
  const { t } = useTranslation();
  const updatePersonStatus = useUpdatePeopleStatusMutation();

  const [reason, setReason] = useState(initialReason);

  useEffect(() => {
    if (reason === "" && initialReason !== "") {
      setReason(initialReason);
    }
  }, [initialReason]);

  if (!person) {
    return null;
  }

  const handleSubmit = () => {
    const status =
      mode === "requalify" ? PersonStatus.NEW : PersonStatus.DISQUALIFIED;
    updatePersonStatus.mutate(
      {
        pathParams: {
          personId: person.id,
          status: status.toString()
        },
        data: {
          reason: reason === "" ? null : reason
        }
      },
      {
        onSuccess: (data: PersonData) => {
          onUpdatePerson(data);
          setIsOpen(false);
        }
      }
    );
  };
  return (
    <Modal
      open={isOpen}
      setIsOpen={setIsOpen}
      contentClassname="w-[35rem]"
      data-test={`people-qualification-modal-${mode}`}
      title={
        <Text
          as="div"
          variant="lgSubtitleH1"
          data-test="people-qualification-modal-title"
        >
          {mode === "edit"
            ? t("people.personView.qualificationModal.editTitle")
            : t("people.personView.qualificationModal.title", {
                action: t(`people.personView.qualificationModal.${mode}`)
              })}
        </Text>
      }
      content={
        <div
          className="h-fit w-[35rem] p-4"
          data-test="people-qualification-modal-content"
        >
          {mode === "requalify" ? (
            <Text variant="bodySecondary">
              {t("people.personView.qualificationModal.requalifySubtitle")}
            </Text>
          ) : (
            <div className="flex flex-col gap-3">
              <div className="flex gap-1">
                <Text variant="bodySecondary">
                  {t(
                    "people.personView.qualificationModal.disqualifyOrEditSubtitle"
                  )}
                </Text>
                <div className="flex gap-[1px]">
                  <ClickableTooltip
                    trigger={
                      <Text variant="smLink">
                        {t("people.personView.qualificationModal.person")}
                      </Text>
                    }
                    content={t("qualifications.person")}
                  />
                  <Text variant="bodySecondary">?</Text>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <div className="flex">
                  <Text variant="labelSecondary">
                    {t("people.personView.qualificationModal.reason")}
                  </Text>
                  <span className="text-primary ml-1">*</span>
                </div>
                <TextareaAutosize
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  minRows={4}
                  className="w-full text-base text-primary placeholder:text-tertiary px-3 py-[6px] bg-surface-field-default border-[0.5px] border-border-field-default rounded-lg resize-none shadow-field transition-all duration-200 ease-in-out hover:border-border-field-hover focus:outline-none focus:border-border-field-focus focus:bg-surface-focus-selected focus:shadow-field-focus"
                />
              </div>
            </div>
          )}
        </div>
      }
      footer={
        <div
          className="flex justify-end gap-2"
          data-test="people-qualification-modal-footer"
        >
          <Button
            data-test="people-qualification-modal-cancel-button"
            variant="secondary"
            onClick={() => setIsOpen(false)}
          >
            {t("cancel")}
          </Button>
          <Button
            data-test="people-qualification-modal-submit-button"
            destructive={mode === "disqualify"}
            onClick={handleSubmit}
            disabled={mode === "disqualify" && isEmpty(reason)}
          >
            {t(`people.personView.qualificationModal.${mode}Button`)}
          </Button>
        </div>
      }
    />
  );
};

export default PersonQualificationModal;
